import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  CardContent,
  Divider,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as AvailableIcon,
  DoNotDisturb as BusyIcon,
  Cancel as OfflineIcon,
  Star as StarIcon,
  Block as BlockIcon,
  CheckCircleOutline as ActivateIcon,
  Logout as LogoutIcon,
  LockReset as PasswordIcon,
  Timer as TimerIcon,
  Pause as PauseIcon,
  EventBusy as LicenseExpiryIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getLicenseExpiryStatus } from '../../utils/licenseExpiry';

import {
  deleteDriver,
  toggleDriverActive,
  toggleDriverAvailability,
  logoutDriver,
  resetDriverPassword
} from '../../redux/slices/driversSlice';
import { isCentralOperationsRole } from '../../redux/slices/authSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color;
  let icon;
  
  switch (status) {
    case 'available':
      color = 'warning'; // Gelb für verfügbar
      icon = <AvailableIcon />;
      break;
    case 'busy':
      color = 'error'; // Rot für besetzt
      icon = <BusyIcon />;
      break;
    case 'offline':
    default:
      color = 'default'; // Grau für offline
      icon = <OfflineIcon />;
      break;
  }
  
  return {
    color: status === 'offline' ? 'inherit' : 'white',
    backgroundColor: status === 'offline' ? undefined : theme.palette[color].main,
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  };
});

const RatingBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  '& .MuiSvgIcon-root': {
    color: theme.palette.warning.main,
    fontSize: '1.2rem',
  },
}));

// Statische Maps – einmalig erstellt, nie neu berechnet
const STATUS_LABEL_MAP = {
  'available': 'Verfügbar',
  'busy': 'Besetzt',
  'offline': 'Nicht verfügbar',
};

const STATUS_PRIORITY = { 'available': 2, 'busy': 1, 'offline': 0 };

// Leitet den Sortierstatus aus isOnline/isAvailable ab
const deriveDriverSortStatus = (driver) => {
  if (!driver.isOnline) return 'offline';
  if (driver.isAvailable) return 'available';
  return 'busy';
};

const DriversList = () => {
  const dispatch = useDispatch();
  // Verwende neues AppData System statt altes drivers slice (memoized)
  const drivers = useSelector(state => state.appData.drivers);
  const isLoading = useSelector(state => state.appData.isLoading.drivers);
  const error = useSelector(state => state.appData.error.drivers);
  const currentUser = useSelector(state => state.auth.user);

  // Passwort zurücksetzen: zentrale Rollen für alle, operator_admin für eigene
  // Fahrer (Liste ist für Operatoren ohnehin bereits operator-gescoped). Die
  // verbindliche Prüfung erfolgt serverseitig in setDriverPassword.
  const canResetPassword = useCallback((driver) => Boolean(
    currentUser && (
      isCentralOperationsRole(currentUser) ||
      (currentUser.role === 'operator_admin' &&
        (!driver?.operatorId || driver.operatorId === currentUser.operatorId))
    )
  ), [currentUser]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('active');
  const [sortBy, setSortBy] = useState('status');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuDriver, setMenuDriver] = useState(null);
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [availabilityDriver, setAvailabilityDriver] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [logoutDriver_, setLogoutDriver_] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordDriver, setPasswordDriver] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordSnackbar, setPasswordSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Flag, um zu verhindern, dass useEffect mehrmals ausgeführt wird
  const [dataLoaded, setDataLoaded] = useState(false);

  // AppDataManager stellt drivers bereit – kein manueller Fetch nötig.
  // dataLoaded wird nur gesetzt um isLoading-State sauber zu halten.
  useEffect(() => {
    if (drivers && drivers.length >= 0) {
      setDataLoaded(true);
    }
  }, [drivers]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSearch = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  }, []);

  const handleStatusFilterChange = useCallback((event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  }, []);

  const handleActiveFilterChange = useCallback((event) => {
    setActiveFilter(event.target.value);
    setPage(0);
  }, []);

  const handleSortByChange = useCallback((event) => {
    setSortBy(event.target.value);
    setPage(0);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuDriver(null);
  }, []);

  const handleOpenMenu = useCallback((event, driver) => {
    setMenuAnchor(event.currentTarget);
    setMenuDriver(driver);
  }, []);

  // Verfügbarkeit (besetzt/verfügbar) — gleicher Flow wie in der Detailansicht:
  // „verfügbar" direkt, „besetzt" mit Auswahl der Pausenart.
  const handleAvailabilityClick = useCallback((driver) => {
    setAvailabilityDriver(driver);
    setAvailabilityDialogOpen(true);
    setMenuAnchor(null);
    setMenuDriver(null);
  }, []);

  const handleAvailabilityCancel = useCallback(() => {
    setAvailabilityDialogOpen(false);
    setAvailabilityDriver(null);
  }, []);

  const handleAvailabilitySetBusy = useCallback((pauseType, pauseDuration) => {
    if (!availabilityDriver) return;
    dispatch(toggleDriverAvailability({
      driverId: availabilityDriver.id,
      setAvailable: false,
      pauseType,
      pauseDuration
    }))
      .unwrap()
      .then(() => setPasswordSnackbar({
        open: true,
        message: `Fahrer als besetzt markiert (${pauseType})`,
        severity: 'success'
      }))
      .catch((err) => setPasswordSnackbar({
        open: true,
        message: typeof err === 'string' ? err : 'Status konnte nicht geändert werden.',
        severity: 'error'
      }));
    setAvailabilityDialogOpen(false);
    setAvailabilityDriver(null);
  }, [dispatch, availabilityDriver]);

  const handleAvailabilitySetAvailable = useCallback(() => {
    if (!availabilityDriver) return;
    dispatch(toggleDriverAvailability({
      driverId: availabilityDriver.id,
      setAvailable: true
    }))
      .unwrap()
      .then(() => setPasswordSnackbar({
        open: true,
        message: 'Fahrer als verfügbar markiert',
        severity: 'success'
      }))
      .catch((err) => setPasswordSnackbar({
        open: true,
        message: typeof err === 'string' ? err : 'Status konnte nicht geändert werden.',
        severity: 'error'
      }));
    setAvailabilityDialogOpen(false);
    setAvailabilityDriver(null);
  }, [dispatch, availabilityDriver]);

  const handleDeleteClick = useCallback((driver) => {
    setSelectedDriver(driver);
    setDeleteDialogOpen(true);
    setMenuAnchor(null);
    setMenuDriver(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedDriver) {
      dispatch(deleteDriver(selectedDriver.id));
      setDeleteDialogOpen(false);
      setSelectedDriver(null);
    }
  }, [dispatch, selectedDriver]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedDriver(null);
  }, []);

  const handleToggleActive = useCallback((driver) => {
    dispatch(toggleDriverActive({ id: driver.id, isActive: driver.isActive === false }));
    setMenuAnchor(null);
    setMenuDriver(null);
  }, [dispatch]);

  const handleLogoutClick = useCallback((driver) => {
    setLogoutDriver_(driver);
    setLogoutDialogOpen(true);
    setMenuAnchor(null);
    setMenuDriver(null);
  }, []);

  const handlePasswordClick = useCallback((driver) => {
    setPasswordDriver(driver);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordDialogOpen(true);
    setMenuAnchor(null);
    setMenuDriver(null);
  }, []);

  const handlePasswordDialogClose = useCallback(() => {
    if (passwordSubmitting) return;
    setPasswordDialogOpen(false);
    setPasswordDriver(null);
  }, [passwordSubmitting]);

  const handlePasswordResetConfirm = useCallback(async () => {
    if (newPassword.length < 6) {
      setPasswordError('Das Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Die Passwörter stimmen nicht überein.');
      return;
    }
    if (!passwordDriver) return;
    setPasswordError('');
    setPasswordSubmitting(true);
    try {
      await dispatch(resetDriverPassword({ driverId: passwordDriver.id, newPassword })).unwrap();
      setPasswordDialogOpen(false);
      setPasswordDriver(null);
      setPasswordSnackbar({
        open: true,
        message: 'Passwort wurde gesetzt. Bitte dem Fahrer mitteilen.',
        severity: 'success'
      });
    } catch (err) {
      setPasswordError(typeof err === 'string' ? err : 'Passwort konnte nicht gesetzt werden.');
    } finally {
      setPasswordSubmitting(false);
    }
  }, [dispatch, newPassword, confirmPassword, passwordDriver]);

  const handleLogoutConfirm = useCallback(() => {
    if (logoutDriver_) {
      dispatch(logoutDriver(logoutDriver_.id));
    }
    setLogoutDialogOpen(false);
    setLogoutDriver_(null);
  }, [dispatch, logoutDriver_]);

  const handleLogoutCancel = useCallback(() => {
    setLogoutDialogOpen(false);
    setLogoutDriver_(null);
  }, []);

  const translateStatus = useCallback((status) => STATUS_LABEL_MAP[status] || status, []);

  // Filter und Sort – wird nur neu berechnet wenn drivers oder Filterkriterien sich ändern.
  // Echtzeit-Updates vom AppDataManager triggern automatisch eine Neuberechnung,
  // da `drivers` als Dependency enthalten ist.
  const filteredData = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return drivers.filter(driver => {
      const searchMatch =
        driver.driverNumber?.toLowerCase().includes(term) ||
        driver.name?.toLowerCase().includes(term) ||
        driver.phone?.includes(searchTerm);
      const statusMatch =
        statusFilter === 'all' ||
        (statusFilter === 'online' && driver.isOnline) ||
        (statusFilter === 'available' && driver.isOnline && driver.isAvailable) ||
        (statusFilter === 'busy' && driver.isOnline && !driver.isAvailable) ||
        (statusFilter === 'offline' && !driver.isOnline);
      const isInactive = driver.isActive === false;
      const activeMatch =
        activeFilter === 'all' ||
        (activeFilter === 'active' && !isInactive) ||
        (activeFilter === 'inactive' && isInactive);
      return driver.isDriver === true && searchMatch && statusMatch && activeMatch;
    }).sort((a, b) => {
      switch (sortBy) {
        case 'driver_number':
          return (a.driverNumber || '').localeCompare(b.driverNumber || '', undefined, { numeric: true });
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'rides':
          return (b.total_rides || 0) - (a.total_rides || 0);
        case 'status': {
          const statusDiff = (STATUS_PRIORITY[deriveDriverSortStatus(b)] ?? 0) - (STATUS_PRIORITY[deriveDriverSortStatus(a)] ?? 0);
          if (statusDiff !== 0) return statusDiff;
          return (a.name || '').localeCompare(b.name || '');
        }
        default:
          return 0;
      }
    });
  }, [drivers, searchTerm, statusFilter, activeFilter, sortBy]);

  // Pagination – neu berechnet nur wenn filteredData, page oder rowsPerPage sich ändern
  const paginatedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );
  
  if (isLoading) {
    return <Loading message="Fahrer werden geladen..." />;
  }
  
  if (error) {
    console.error('Fehler in DriversList Component:', error);
    return (
      <ErrorMessage
        title="Fehler beim Laden der Fahrer"
        message={error.message || error.details || JSON.stringify(error)}
        // AppDataManager versucht automatisch erneut - kein manueller Retry nötig
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 } }}>
      <PageHeader
        title="Fahrerverwaltung"
        description="Übersicht und Verwaltung aller Fahrer"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrer', path: '/fahrer' }
        ]}
        actionButton={{
          label: 'Fahrer hinzufügen',
          icon: <AddIcon />,
          path: '/fahrer/neu'
        }}
      />
      
      {/* Filter und Suche */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, md: 3 } }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Suche nach Nummer, Name oder Telefon"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">Alle Status</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="available">Verfügbar</MenuItem>
                <MenuItem value="busy">Besetzt</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="active-filter-label">Kontostatus</InputLabel>
              <Select
                labelId="active-filter-label"
                id="active-filter"
                value={activeFilter}
                label="Kontostatus"
                onChange={handleActiveFilterChange}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="active">Nur aktive</MenuItem>
                <MenuItem value="inactive">Nur deaktivierte</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="sort-by-label">Sortieren nach</InputLabel>
              <Select
                labelId="sort-by-label"
                id="sort-by"
                value={sortBy}
                label="Sortieren nach"
                onChange={handleSortByChange}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="driver_number">Fahrernummer</MenuItem>
                <MenuItem value="rating">Bewertung</MenuItem>
                <MenuItem value="rides">Anzahl der Fahrten</MenuItem>
                <MenuItem value="status">Status</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {filteredData.length} Fahrer gefunden
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Mobile Fahrerkarten */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
        {paginatedData.length > 0 ? (
          paginatedData.map((driver) => {
            const driverStatus = driver.isOnline ? (driver.isAvailable ? 'available' : 'busy') : 'offline';
            return (
              <Card key={driver.id} variant="outlined">
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', gap: 1.25, minWidth: 0 }}>
                      <Avatar src={driver.profilePic || ''} sx={{ opacity: driver.isActive === false ? 0.5 : 1 }}>
                        {driver.name ? driver.name.charAt(0) : 'F'}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
                            {driver.name || 'Unbekannter Fahrer'}
                          </Typography>
                          <Chip
                            label={driver.driverNumber || '-'}
                            size="small"
                            variant={driver.driverNumber ? 'filled' : 'outlined'}
                            color={driver.driverNumber ? 'primary' : 'default'}
                            sx={{ height: 22 }}
                          />
                          {driver.isActive === false && (
                            <Chip
                              label="Deaktiviert"
                              size="small"
                              icon={<BlockIcon />}
                              color="error"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                          )}
                          {(() => {
                            const lic = getLicenseExpiryStatus(driver.licenseExpiry);
                            return lic ? (
                              <Chip
                                label={lic.label}
                                size="small"
                                icon={<LicenseExpiryIcon />}
                                color={lic.color}
                                variant="outlined"
                                sx={{ height: 22, fontSize: '0.7rem' }}
                              />
                            ) : null;
                          })()}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                          {driver.email || 'Keine E-Mail'}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      aria-label="Aktionen"
                      aria-controls={`driver-menu-${driver.id}`}
                      aria-haspopup="true"
                      onClick={(e) => handleOpenMenu(e, driver)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  <Divider sx={{ my: 1.25 }} />

                  <Grid container spacing={1}>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Box sx={{ mt: 0.25 }}>
                        <StatusChip
                          label={translateStatus(driverStatus)}
                          status={driverStatus}
                          size="small"
                          icon={driver.isOnline ? (driver.isAvailable ? <AvailableIcon /> : <BusyIcon />) : <OfflineIcon />}
                        />
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Online</Typography>
                      <Box sx={{ mt: 0.25 }}>
                        <Chip
                          label={driver.isOnline ? 'Online' : 'Offline'}
                          color={driver.isOnline ? 'success' : 'default'}
                          size="small"
                          icon={driver.isOnline ? <AvailableIcon /> : <OfflineIcon />}
                        />
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Bewertung</Typography>
                      <RatingBox sx={{ mt: 0.25 }}>
                        <StarIcon />
                        <Typography variant="body2">{driver.rating || 'N/A'}</Typography>
                      </RatingBox>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Fahrten</Typography>
                      <Typography variant="body2">{driver.total_rides || 0}</Typography>
                    </Grid>
                    <Grid size={12}>
                      <Typography variant="caption" color="text.secondary">Telefon</Typography>
                      <Typography variant="body2">{driver.phone || 'Nicht angegeben'}</Typography>
                    </Grid>
                  </Grid>

                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    component={Link}
                    to={`/fahrer/${driver.id}`}
                    sx={{ mt: 1.5 }}
                  >
                    Details anzeigen
                  </Button>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1">Keine Fahrer gefunden</Typography>
          </Paper>
        )}
        <TablePagination
          rowsPerPageOptions={[10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Pro Seite:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '.MuiTablePagination-toolbar': {
              px: 1,
              flexWrap: 'wrap',
              justifyContent: 'center',
            },
          }}
        />
      </Box>

      {/* Desktop Fahrertabelle */}
      <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Table sx={{ minWidth: 650 }} aria-label="Fahrertabelle">
          <TableHead>
            <TableRow>
              <TableCell align="center">Nr.</TableCell>
              <TableCell>Fahrer</TableCell>
              <TableCell align="center">Online</TableCell>
              <TableCell align="center">Verfügbar</TableCell>
              <TableCell align="center">Bewertung</TableCell>
              <TableCell align="center">Fahrten</TableCell>
              <TableCell align="center">Telefon</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((driver) => (
                <StyledTableRow key={driver.id}>
                  <TableCell align="center">
                    <Chip
                      label={driver.driverNumber || '-'}
                      size="small"
                      variant={driver.driverNumber ? 'filled' : 'outlined'}
                      color={driver.driverNumber ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={driver.profilePic || ''} sx={{ opacity: driver.isActive === false ? 0.5 : 1 }}>
                        {driver.name ? driver.name.charAt(0) : 'F'}
                      </Avatar>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">{driver.name}</Typography>
                          {driver.isActive === false && (
                            <Chip
                              label="Deaktiviert"
                              size="small"
                              icon={<BlockIcon />}
                              color="error"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                          {(() => {
                            const lic = getLicenseExpiryStatus(driver.licenseExpiry);
                            return lic ? (
                              <Chip
                                label={lic.label}
                                size="small"
                                icon={<LicenseExpiryIcon />}
                                color={lic.color}
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            ) : null;
                          })()}
                        </Box>
                        <Typography variant="body2" color="textSecondary">{driver.email}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={driver.isOnline ? "Online" : "Offline"}
                      color={driver.isOnline ? "success" : "default"}
                      size="small"
                      icon={driver.isOnline ? <AvailableIcon /> : <OfflineIcon />}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <StatusChip 
                      label={driver.isOnline ? (driver.isAvailable ? 'Verfügbar' : 'Besetzt') : 'Offline'}
                      status={driver.isOnline ? (driver.isAvailable ? 'available' : 'busy') : 'offline'}
                      size="small"
                      icon={driver.isOnline ? (driver.isAvailable ? <AvailableIcon /> : <BusyIcon />) : <OfflineIcon />}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <RatingBox>
                      <StarIcon />
                      <Typography>{driver.rating || 'N/A'}</Typography>
                    </RatingBox>
                  </TableCell>
                  <TableCell align="center">{driver.total_rides || 0}</TableCell>
                  <TableCell align="center">{driver.phone}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="Aktionen"
                      aria-controls={`driver-menu-${driver.id}`}
                      aria-haspopup="true"
                      onClick={(e) => handleOpenMenu(e, driver)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" sx={{ py: 3 }}>
                    Keine Fahrer gefunden
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={
            ({ from, to, count }) => `${from}–${to} von ${count}`
          }
        />
      </TableContainer>
      
      {/* Aktions-Menü */}
      <Menu
        id={menuDriver ? `driver-menu-${menuDriver.id}` : undefined}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem 
          component={Link} 
          to={menuDriver ? `/fahrer/${menuDriver.id}` : '#'} 
          onClick={handleCloseMenu}
        >
          Fahrer anzeigen
        </MenuItem>
        <MenuItem 
          component={Link} 
          to={menuDriver ? `/fahrer/${menuDriver.id}/bearbeiten` : '#'} 
          onClick={handleCloseMenu}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Bearbeiten
        </MenuItem>
        {menuDriver?.isOnline && (
          <MenuItem
            onClick={() => menuDriver && handleAvailabilityClick(menuDriver)}
            sx={{ color: menuDriver?.isAvailable ? 'warning.main' : 'success.main' }}
          >
            {menuDriver?.isAvailable ? (
              <><BusyIcon fontSize="small" sx={{ mr: 1 }} />Als besetzt markieren</>
            ) : (
              <><AvailableIcon fontSize="small" sx={{ mr: 1 }} />Als verfügbar markieren</>
            )}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => menuDriver && handleToggleActive(menuDriver)}
          sx={{ color: menuDriver?.isActive === false ? 'success.main' : 'warning.main' }}
        >
          {menuDriver?.isActive === false ? (
            <><ActivateIcon fontSize="small" sx={{ mr: 1 }} />Aktivieren</>
          ) : (
            <><BlockIcon fontSize="small" sx={{ mr: 1 }} />Deaktivieren</>
          )}
        </MenuItem>
        {menuDriver?.isOnline && (
          <MenuItem
            onClick={() => menuDriver && handleLogoutClick(menuDriver)}
            sx={{ color: 'primary.main' }}
          >
            <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
            Fahrer abmelden
          </MenuItem>
        )}
        {menuDriver && canResetPassword(menuDriver) && (
          <MenuItem
            onClick={() => menuDriver && handlePasswordClick(menuDriver)}
            sx={{ color: 'primary.main' }}
          >
            <PasswordIcon fontSize="small" sx={{ mr: 1 }} />
            Passwort zurücksetzen
          </MenuItem>
        )}
        <MenuItem
          onClick={() => menuDriver && handleDeleteClick(menuDriver)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Löschen
        </MenuItem>
      </Menu>

      {/* Verfügbarkeit (besetzt/verfügbar) — gleicher Flow wie Detailansicht */}
      <Dialog open={availabilityDialogOpen} onClose={handleAvailabilityCancel} maxWidth="sm">
        {availabilityDriver?.isAvailable ? (
          <>
            <DialogTitle>Fahrer besetzt setzen</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Pausenart für {availabilityDriver?.name || 'Fahrer'} auswählen:
              </DialogContentText>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="outlined" startIcon={<TimerIcon />} onClick={() => handleAvailabilitySetBusy('Stadtfahrt', 15)} fullWidth>
                  Stadtfahrt ca. 15 min
                </Button>
                <Button variant="outlined" startIcon={<TimerIcon />} onClick={() => handleAvailabilitySetBusy('Pause', 30)} fullWidth>
                  Pause ca. 30 min
                </Button>
                <Button variant="outlined" startIcon={<TimerIcon />} onClick={() => handleAvailabilitySetBusy('Langstrecke', 45)} fullWidth>
                  Langstrecke ca. 45 min
                </Button>
                <Button variant="outlined" color="warning" startIcon={<PauseIcon />} onClick={() => handleAvailabilitySetBusy('Disposition', null)} fullWidth>
                  Disposition (unbefristet)
                </Button>
              </Box>
              <DialogContentText sx={{ mt: 2, fontSize: '0.85rem', color: 'text.secondary' }}>
                Die mobile App wird den Status automatisch aktualisieren.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleAvailabilityCancel}>Abbrechen</Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Fahrer verfügbar setzen</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Möchten Sie {availabilityDriver?.name || 'den Fahrer'} wieder als verfügbar markieren?
                Die mobile App wird den Status automatisch aktualisieren.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleAvailabilityCancel}>Abbrechen</Button>
              <Button onClick={handleAvailabilitySetAvailable} variant="contained" color="success">
                Verfügbar setzen
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Passwort-zurücksetzen-Dialog */}
      <Dialog open={passwordDialogOpen} onClose={handlePasswordDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>Passwort zurücksetzen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Neues Login-Passwort für {passwordDriver?.user?.name || passwordDriver?.name || 'den Fahrer'}
            {(passwordDriver?.user?.email || passwordDriver?.email) ? ` (${passwordDriver?.user?.email || passwordDriver?.email})` : ''} festlegen.
            Das Passwort wird nicht gespeichert – bitte dem Fahrer mitteilen.
          </Typography>
          <TextField
            label="Neues Passwort"
            type="text"
            fullWidth
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }}
            disabled={passwordSubmitting}
          />
          <TextField
            label="Passwort bestätigen"
            type="text"
            fullWidth
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={passwordSubmitting}
          />
          {passwordError && (
            <Alert severity="error" sx={{ mt: 2 }}>{passwordError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordDialogClose} disabled={passwordSubmitting}>Abbrechen</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePasswordResetConfirm}
            disabled={passwordSubmitting}
          >
            {passwordSubmitting ? 'Wird gesetzt…' : 'Passwort setzen'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={passwordSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setPasswordSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setPasswordSnackbar(prev => ({ ...prev, open: false }))}
          severity={passwordSnackbar.severity}
          sx={{ width: '100%' }}
        >
          {passwordSnackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Löschen-Dialog */}
      {/* Fahrer abmelden Dialog */}
      <Dialog open={logoutDialogOpen} onClose={handleLogoutCancel} maxWidth="sm">
        <DialogTitle>Fahrer abmelden</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1">
              Möchten Sie den Fahrer {logoutDriver_?.user?.name} wirklich abmelden?
              Die mobile App wird den Fahrer automatisch abmelden.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel}>Abbrechen</Button>
          <Button onClick={handleLogoutConfirm} variant="contained" color="primary">
            Fahrer abmelden
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Fahrer löschen"
        content={`Möchten Sie den Fahrer ${selectedDriver?.user?.name || ''} wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default DriversList;
